/**
 * Os avisos sonoros da sala.
 *
 * Sao sintetizados na hora, com dois osciladores e um envelope — nao ha arquivo
 * de audio no projeto. Isso e de proposito: cada MP3 seria mais um download no
 * primeiro acesso e mais um arquivo pro service worker guardar, pra tocar meio
 * segundo de bipe. O que se ganha em troca e poder afinar o som mudando um
 * numero aqui, sem abrir editor de audio nenhum.
 *
 * O desenho segue o que todo mundo ja conhece de programa de chamada: subindo
 * quando alguem CHEGA ou algo COMECA, descendo quando sai ou termina. Ninguem
 * precisa aprender o significado de cada um — a direcao ja conta.
 */

export type SomId =
    | 'voz-entrou'
    | 'voz-saiu'
    | 'transmissao-comecou'
    | 'transmissao-parou'
    | 'mensagem'

interface Nota {
    /** Frequencia em hertz. */
    hz: number
    /** Quando comeca, em segundos contados do disparo. */
    em: number
    /** Quanto dura, em segundos. */
    dura: number
}

/**
 * Volume geral.
 *
 * Baixo de proposito: isto toca por cima de uma conversa, e um aviso que cobre
 * a voz de quem esta falando vira estorvo na segunda vez que acontece.
 */
const VOLUME = 0.09

const SONS: Record<SomId, Nota[]> = {
    // Subindo: chegou.
    'voz-entrou': [
        { hz: 587.33, em: 0, dura: 0.09 },
        { hz: 880.0, em: 0.08, dura: 0.13 }
    ],
    // A mesma coisa ao contrario: saiu.
    'voz-saiu': [
        { hz: 880.0, em: 0, dura: 0.09 },
        { hz: 587.33, em: 0.08, dura: 0.15 }
    ],
    // Tres notas, e nao duas: separa "comecou a transmitir" de "entrou na voz"
    // mesmo pra quem nao esta olhando pra tela.
    'transmissao-comecou': [
        { hz: 523.25, em: 0, dura: 0.08 },
        { hz: 659.25, em: 0.07, dura: 0.08 },
        { hz: 783.99, em: 0.14, dura: 0.16 }
    ],
    'transmissao-parou': [
        { hz: 783.99, em: 0, dura: 0.08 },
        { hz: 659.25, em: 0.07, dura: 0.08 },
        { hz: 523.25, em: 0.14, dura: 0.16 }
    ],
    // Uma nota so, curta e aguda: mensagem chega muito mais vezes que o resto.
    mensagem: [{ hz: 987.77, em: 0, dura: 0.11 }]
}

let contexto: AudioContext | null = null

/**
 * O `AudioContext`, criado na primeira vez que ha o que tocar.
 *
 * Criar um por som vazaria: o navegador limita quantos existem ao mesmo tempo,
 * e uma sala de uma hora dispara centenas de avisos.
 */
function pegarContexto(): AudioContext | null {
    if (typeof window === 'undefined') return null

    const Constructor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    // Sem Web Audio (jsdom nos testes, navegador antigo) a sala inteira segue
    // funcionando — o que se perde e o aviso sonoro, e so.
    if (!Constructor) return null

    contexto ??= new Constructor()
    return contexto
}

export function tocarSom(id: SomId): void {
    const ctx = pegarContexto()
    if (!ctx) return

    /*
     * O navegador so libera audio depois de um gesto da pessoa. Entrar na sala
     * ja e um clique, entao na pratica o contexto nasce liberado; quando nao
     * nasce (aba restaurada, autoplay bloqueado), o `resume` destrava e as
     * notas agendadas tocam quando ele voltar.
     */
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {})

    // Uma folga minima: agendar exatamente em `currentTime` faz alguns
    // navegadores comerem o ataque da primeira nota.
    const inicio = ctx.currentTime + 0.02

    for (const nota of SONS[id]) {
        const oscilador = ctx.createOscillator()
        const ganho = ctx.createGain()

        // Senoide: sem harmonicos, e o que soa mais perto de um bipe de
        // interface. Onda quadrada no mesmo volume soaria como brinquedo.
        oscilador.type = 'sine'
        oscilador.frequency.value = nota.hz

        const comeca = inicio + nota.em
        const termina = comeca + nota.dura

        /*
         * Envelope curto. Sem ele o som comeca e termina em degrau, e o
         * degrau vira um "clique" audivel — o mesmo estalo de cabo de fone
         * sendo puxado.
         */
        ganho.gain.setValueAtTime(0.0001, comeca)
        ganho.gain.exponentialRampToValueAtTime(VOLUME, comeca + 0.012)
        ganho.gain.exponentialRampToValueAtTime(0.0001, termina)

        oscilador.connect(ganho).connect(ctx.destination)
        oscilador.start(comeca)
        oscilador.stop(termina + 0.02)
    }
}
